import {
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { BaseComponent } from '@core/base/base.component';
import { AuthService } from '@core/services/auth.service';
import {
  LucideBell,
  LucideKey,
  LucideLogOut,
  LucideMenu,
} from '@lucide/angular';
import { TranslateModule } from '@ngx-translate/core';
import { ChangePasswordModalComponent } from '../../../shared/components/change-password-modal/change-password-modal.component';
import { NotificationDialogComponent } from '../../../shared/components/notification-dialog/notification-dialog.component';
import { LayoutService } from '../../services/layout.service';
import { NotificationService } from '../../services/notification.service';
import { AuthStore } from '../../stores/auth.store';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [LucideMenu, LucideBell, LucideLogOut, LucideKey, TranslateModule],
  template: `
    <header
      class="navbar-header bg-white border-bottom px-4 d-flex align-items-center shadow-sm"
    >
      <button
        class="btn text-dark p-0 me-3 shadow-none"
        (click)="layoutService.toggleSidebar()"
        [attr.aria-label]="'COMMON.TOGGLE_MENU' | translate"
      >
        <svg lucideMenu [size]="24"></svg>
      </button>

      <div class="ms-auto d-flex align-items-center gap-3">
        <button
          class="btn text-secondary position-relative p-1 shadow-none"
          [attr.aria-label]="'COMMON.NOTIFICATION' | translate"
          (click)="openNotificationDialog()"
        >
          <svg lucideBell [size]="20"></svg>
          @if (notificationService.unreadCount() > 0) {
            <span
              class="position-absolute badge rounded-pill bg-danger border border-light notification-badge"
            >
              {{
                notificationService.unreadCount() > 99
                  ? '99+'
                  : notificationService.unreadCount()
              }}
            </span>
          }
        </button>

        <div class="position-relative">
          <div
            class="user-profile d-flex align-items-center gap-2 ps-3 border-start cursor-pointer"
            (click)="toggleProfileMenu($event)"
          >
            <div
              class="avatar bg-primary text-white rounded-circle d-flex align-items-center justify-content-center fw-bold overflow-hidden shadow-sm"
            >
              <img
                [src]="authStore.user()?.avatar || 'images/user.png'"
                class="w-100 h-100 object-fit-cover"
                alt="Profile"
              />
            </div>

            <div class="user-info d-none d-sm-block lh-sm">
              <div class="fw-bold small text-dark">
                {{
                  authStore.user()?.full_name || ('COMMON.GUEST' | translate)
                }}
              </div>
              <div class="text-secondary">
                {{ authStore.user()?.user_name }}
              </div>
            </div>
          </div>

          @if (showProfileMenu()) {
            <div
              class="dropdown-menu-custom bg-white border rounded shadow-lg py-1 position-absolute end-0 mt-2 show animate-fade-in minw-180"
            >
              <button
                class="dropdown-item d-flex align-items-center gap-2 px-3 py-2"
                (click)="onChangePassword()"
              >
                <svg lucideKey [size]="14" class="text-secondary"></svg>
                <span class="small">{{
                  'COMMON.CHANGE_PASSWORD' | translate
                }}</span>
              </button>

              <div class="dropdown-divider"></div>

              <button
                class="dropdown-item d-flex align-items-center gap-2 px-3 py-2 text-danger"
                (click)="onLogout()"
              >
                <svg lucideLogOut [size]="14"></svg>
                <span class="small fw-semibold">{{
                  'COMMON.LOGOUT' | translate
                }}</span>
              </button>
            </div>
          }
        </div>
      </div>
    </header>
  `,
  styleUrl: './navbar.component.scss',
})
export class NavbarComponent extends BaseComponent implements OnInit {
  layoutService = inject(LayoutService);
  authStore = inject(AuthStore);
  notificationService = inject(NotificationService);
  authService = inject(AuthService);
  router = inject(Router);
  el = inject(ElementRef);

  showProfileMenu = signal(false);

  // TODO: Bỏ nếu dùng avatar từ public/images/user.png
  userInitials = computed(() => {
    const user = this.authStore.user();
    if (!user) return 'AD';

    const name = user.full_name || user.user_name || 'Admin';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  });

  ngOnInit(): void {
    this.subscribeSafe(this.notificationService.loadUnreadCount());
  }

  toggleProfileMenu(event: Event) {
    event.stopPropagation();
    this.showProfileMenu.set(!this.showProfileMenu());
  }

  @HostListener('document:click', ['$event'])
  closeMenu(event: Event) {
    if (!this.el.nativeElement.contains(event.target)) {
      this.showProfileMenu.set(false);
    }
  }

  openNotificationDialog() {
    this.showProfileMenu.set(false);
    this.modalService.open(NotificationDialogComponent, {
      centered: true,
      size: 'lg',
      scrollable: true,
    });
  }

  onChangePassword() {
    this.showProfileMenu.set(false);
    this.modalService.open(ChangePasswordModalComponent, {
      centered: true,
      backdrop: 'static',
      size: 'md',
    });
  }

  onLogout() {
    this.showProfileMenu.set(false);
    this.authService.logout();
  }
}
